import { createTRPCClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "@aucash/api/router";

import { API_URL } from "./env";
import { reportError } from "./observa/reporter";

// Retry only when fetch itself throws — i.e. the request never reached
// the server (DNS failure, transient connection drop, captive-portal
// blip). We deliberately do NOT retry on 5xx: those mean the server saw
// the request, and once persistence lands a mutation like calc.au08 →
// Assessment row could have already committed. Re-firing would double
// the write. Better to surface the error and let the user retry knowingly.
//
// Backoff: 200ms, 400ms. Anything longer reads as a hang.
const RETRY_DELAYS_MS = [200, 400];

async function retryingFetch(
  url: RequestInfo | URL,
  options?: RequestInit,
): Promise<Response> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await fetch(url, { ...options, credentials: "include" });
    } catch (err) {
      lastErr = err;
      const delay = RETRY_DELAYS_MS[attempt];
      if (delay === undefined) break;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  // Every retry exhausted — the request never reached the server (API/Cloudflare
  // down, user offline). The server can't log what it never received, and the
  // calling screen swallows this into a local error banner, so report it here:
  // this is the one failure mode that's otherwise invisible to error tracking.
  reportError(lastErr, { kind: "trpc-network", url: String(url) });
  throw lastErr;
}

// Forward the observa browser session id — the same one the app SDK stamps on
// client events (it lives in sessionStorage under 'observa.session.v1', shape
// { id }). The API's observa request hook reads `x-observa-session-id`, so
// server-side events (calculator.run, email.captured, report.*) get tagged with
// the SAME sessionId as the app's result.viewed / compare.clicked. That's what
// lets the anonymous cross-surface funnel stitch by sessionId. Web only —
// native has no sessionStorage, where the funnel falls back to userId post-auth.
function observaSessionHeaders(): Record<string, string> {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return {};
    const raw = window.sessionStorage.getItem("observa.session.v1");
    if (raw === null) return {};
    const id = (JSON.parse(raw) as { id?: string }).id;
    return typeof id === "string" && id !== "" ? { "x-observa-session-id": id } : {};
  } catch {
    return {};
  }
}

export const trpc = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      fetch: retryingFetch,
      headers: observaSessionHeaders,
    }),
  ],
});
